/**
 * Labstep
 *
 * @module components/ProtocolCollection/Action/Menu
 * @desc Menu for protocol collection
 */

import EntityActionPrint from 'labstep-web/components/Entity/Action/Print';
import EntityActionRevoke from 'labstep-web/components/Entity/Action/Revoke';
import { EntityActionTag } from 'labstep-web/components/Entity/Action/Tag';
import { EntityActionTransferOwnership } from 'labstep-web/components/Entity/Action/TransferOwnership';
import { EntityActionUpdateAllowedActionsLock } from 'labstep-web/components/Entity/Action/Update/AllowedActionsLock';
import { EntityUserActionManage } from 'labstep-web/components/EntityUser/Action/Manage';
import { FolderActionAdd } from 'labstep-web/components/Folder/Action/Add';
import { FolderActionRemove } from 'labstep-web/components/Folder/Action/Remove';
import ProtocolActionAddToExperiment from 'labstep-web/components/Protocol/Action/AddToExperiment';
import ProtocolActionDuplicate from 'labstep-web/components/Protocol/Action/Duplicate';
import { ProtocolCollectionActionDelete as Delete } from 'labstep-web/components/ProtocolCollection/Action/Delete';
import { ProtocolCollectionActionShare } from 'labstep-web/components/ProtocolCollection/Action/Share';
import { ProtocolCollectionActionView } from 'labstep-web/components/ProtocolCollection/Action/View';
import ProtocolConditionActionCompareData from 'labstep-web/components/ProtocolCondition/Action/CompareData';
import { ActionMenu } from 'labstep-web/core/Action/Menu';
import type React from 'react';
import type { IProtocolCollectionActionMenuProps } from './types';

export enum Action {
  duplicate = 'duplicate',
  view = 'view',
  share = 'share',
  add_to_experiment = 'add_to_experiment',
  print = 'print',
  assign = 'assign',
  tag = 'tag',
  delete = 'delete',
  revoke = 'revoke',
  transfer_ownership = 'transfer_ownership',
  add_to_folder = 'add_to_folder',
  remove_from_folder = 'remove_from_folder',
  allowed_actions_lock_settings = 'allowed_actions_lock_settings',
  reporting = 'reporting',
  save_as_template = 'save_as_template',
  protocol_from_template = 'protocol_from_template',
}

export const ProtocolCollectionActionMenu: React.FC<
  IProtocolCollectionActionMenuProps
> = (props) => {
  const { protocolCollection, actions } = props;

  const protocol = protocolCollection.last_or_draft_version;

  return (
    <ActionMenu
      type="icon"
      dataCy="components/ProtocolCollection/Action/Menu"
    >
      {actions.includes(Action.allowed_actions_lock_settings) && (
        <EntityActionUpdateAllowedActionsLock
          entity={protocolCollection}
        />
      )}
      {actions.includes(Action.reporting) && (
        <ProtocolConditionActionCompareData
          actionComponentType="option"
          protocolCollection={protocol.collection}
        />
      )}
      {actions.includes(Action.add_to_experiment) &&
        protocolCollection.last_version && (
          <ProtocolActionAddToExperiment
            protocol={protocolCollection.last_version}
          />
        )}
      {actions.includes(Action.view) && (
        <ProtocolCollectionActionView
          protocolCollection={protocolCollection}
        />
      )}
      {actions.includes(Action.share) && (
        <ProtocolCollectionActionShare
          protocolCollection={protocolCollection}
        />
      )}
      {actions.includes(Action.duplicate) && (
        <ProtocolActionDuplicate
          protocol={protocol}
          isTemplate={protocolCollection.is_template}
          header={
            protocolCollection.is_template
              ? 'Duplicate Template'
              : 'Duplicate Protocol'
          }
        />
      )}
      {actions.includes(Action.save_as_template) && (
        <ProtocolActionDuplicate
          protocol={protocol}
          isTemplate={true}
          header="Save as Template"
          actionComponentProps={{
            text: 'Save as Template',
            type: 'option',
            icon: 'save',
          }}
        />
      )}
      {actions.includes(Action.protocol_from_template) && (
        <ProtocolActionDuplicate
          protocol={protocol}
          isTemplate={false}
          header="New Protocol from Template"
          actionComponentProps={{
            text: 'New Protocol from Template',
            type: 'option',
            icon: 'tasks',
          }}
        />
      )}
      {actions.includes(Action.add_to_folder) && (
        <FolderActionAdd parentEntity={protocolCollection} />
      )}
      {actions.includes(Action.remove_from_folder) && (
        <FolderActionRemove
          parentId={protocolCollection.id}
          parentName={protocolCollection.entityName}
        />
      )}
      {actions.includes(Action.print) && (
        <EntityActionPrint entity={protocol} />
      )}
      {actions.includes(Action.assign) && (
        <EntityUserActionManage entity={protocolCollection} />
      )}
      {actions.includes(Action.tag) && (
        <EntityActionTag parentEntity={protocolCollection} />
      )}
      {actions.includes(Action.revoke) && (
        <EntityActionRevoke entity={protocolCollection} />
      )}
      {actions.includes(Action.transfer_ownership) && (
        <EntityActionTransferOwnership entity={protocolCollection} />
      )}
      {actions.includes(Action.delete) && (
        <Delete protocolCollection={protocolCollection} />
      )}
    </ActionMenu>
  );
};

ProtocolCollectionActionMenu.defaultProps = {
  actions: [],
};
